/*
 * 微信 JS-SDK。
 *
 * 微信内网页要用分享、录音、扫码、支付调起，都得先 `wx.config` 成功。
 * 服务端那半在 `/api/v1/app/wechat/jsapi-signature`（`FEATURES.md` WX-001，
 * 自标「H5 为主体后成为首发依赖，非可选」）。
 *
 * ## 只在微信内做事，其它宿主一律静默跳过
 *
 * 普通浏览器与小程序 web-view 里 `wx.config` 要么不存在要么无意义。
 * 调用方**不需要**先判断宿主——那正是 `host-bridge.ts` 存在的理由：
 * 别的模块不该出现「是不是在微信里」的判断。这里自己问它一次。
 *
 * ## 失败是静默的，而且是对的
 *
 * JS-SDK 配不上时，能降级的都降级：分享卡片退回微信的默认抓取
 * （标题取 `<title>`，图取页面第一张图），录音按钮本来就有能力探测。
 * 弹一个「微信配置失败」对用户没有任何可操作性——他既不知道那是什么，
 * 也做不了任何事。失败只进 console，排障靠它。
 *
 * ## 签名绑 URL，SPA 换页要重签
 *
 * 微信把当前页面地址算进签名。iOS 微信有个众所周知的行为：
 * **`wx.config` 认的是进入页面时的那个 URL，而不是 pushState 之后的**。
 * 所以这里按「进入站点时的那一个 URL」签一次并缓存，不跟着路由变化重签——
 * 跟着重签在 iOS 上反而会失效。真需要按页分享时再解决它。
 */

import { getHostProfile } from "./host-bridge";
import { apiRequest } from "./api-client";

interface JsapiSignature {
  appId: string;
  timestamp: number;
  nonceStr: string;
  signature: string;
  url: string;
}

interface WxSdk {
  config: (options: {
    debug: boolean;
    appId: string;
    timestamp: number;
    nonceStr: string;
    signature: string;
    jsApiList: string[];
  }) => void;
  ready: (cb: () => void) => void;
  error: (cb: (err: unknown) => void) => void;
  updateAppMessageShareData?: (options: ShareOptions) => void;
  updateTimelineShareData?: (options: ShareOptions) => void;
}

interface ShareOptions {
  title: string;
  desc?: string;
  link: string;
  imgUrl: string;
  success?: () => void;
}

declare global {
  interface Window {
    wx?: WxSdk;
  }
}

const SDK_URL = "https://res.wx.qq.com/open/js/jweixin-1.6.0.js";

/**
 * 要申请哪些接口。
 *
 * ⚠ **必须显式列出，用不到的不要写上。** `jsApiList` 里出现未开通的接口
 * 会让整个 `wx.config` 失败（不是那一个失败，是全部），
 * 而错误信息只有一句 `permission denied`。
 *
 * 现在只有分享。加录音（`startRecord` 那一套）时，要先确认服务号已经开通。
 */
const JS_API_LIST = ["updateAppMessageShareData", "updateTimelineShareData"];

/**
 * 进入站点时的地址。
 *
 * **在模块加载时就抓住它**，不是签名时才读 `location.href`：
 * 那时可能已经 pushState 过了，而 iOS 微信认的是进入时的那一个。
 */
const ENTRY_URL = typeof window === "undefined" ? "" : window.location.href;

let readyPromise: Promise<WxSdk | null> | null = null;

/**
 * 确保 JS-SDK 可用。**幂等**，多次调用共用同一次配置。
 *
 * @returns 配好的 `wx`，不可用时是 null（调用方据此跳过，不要抛）。
 */
export function ensureWxJsSdk(): Promise<WxSdk | null> {
  readyPromise ??= configure().catch((error: unknown) => {
    console.warn("[wx] JS-SDK 未能配置，相关能力降级", error);
    return null;
  });
  return readyPromise;
}

async function configure(): Promise<WxSdk | null> {
  if (typeof window === "undefined") return null;
  // 别的模块不该判断宿主，这里是唯一允许问的地方。
  if (getHostProfile().kind !== "wechat-h5") return null;

  const [signature] = await Promise.all([
    apiRequest<JsapiSignature>("/api/v1/app/wechat/jsapi-signature", {
      body: { url: ENTRY_URL },
    }),
    loadScript(),
  ]);

  const wx = window.wx;
  if (!wx) throw new Error("jweixin 脚本已加载但 window.wx 不存在");

  return new Promise<WxSdk>((resolve, reject) => {
    // config 是异步的，成败只能靠这两个回调知道。没有超时的话，
    // 微信在某些版本里两个都不回调，这个 Promise 会永远挂着。
    const timer = setTimeout(() => reject(new Error("wx.config 超时")), 10_000);

    wx.ready(() => {
      clearTimeout(timer);
      resolve(wx);
    });
    wx.error((err) => {
      clearTimeout(timer);
      reject(err instanceof Error ? err : new Error(JSON.stringify(err)));
    });

    wx.config({
      // debug 会在微信里弹一连串 alert，任何时候都不要在生产打开。
      debug: false,
      appId: signature.appId,
      timestamp: signature.timestamp,
      nonceStr: signature.nonceStr,
      signature: signature.signature,
      jsApiList: JS_API_LIST,
    });
  });
}

function loadScript(): Promise<void> {
  if (window.wx) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("jweixin 脚本加载失败"));
    document.head.appendChild(script);
  });
}

/**
 * 设置分享卡片（发给朋友 / 分享到朋友圈）。
 *
 * 不设的话微信会自己抓：标题取 `<title>`，摘要取页面前几十个字，
 * 图取第一张符合尺寸的图——对一个 SPA 来说抓出来的东西通常很难看。
 *
 * ⚠ **`link` 必须在 JS 接口安全域名下**，否则微信会拒绝设置。
 * 这里固定用站点根地址而不是当前页：分享出去的链接应当把人带到入口，
 * 而不是带到分享者当时停留的某个二级页（那些页面对方多半没有权限看）。
 */
export async function setShareCard(card: {
  title: string;
  desc: string;
  imgUrl: string;
}): Promise<void> {
  const wx = await ensureWxJsSdk();
  if (!wx) return;

  const link = `${window.location.origin}/`;
  const options: ShareOptions = { ...card, link };

  // 两个接口分别对应「发给朋友」与「分享到朋友圈」，微信没有合并的写法。
  wx.updateAppMessageShareData?.(options);
  wx.updateTimelineShareData?.(options);
}
